/**
 * GMA-1629 (CompassCare Web 14/14) — Audit + Settings route container.
 *
 * Mounts the app-shell (WebShell) around the combined Audit Log + Settings
 * screen (AuditSettingsPage). ONE component serves BOTH sidebar entries —
 * /web/audit (Audit Log tab preselected) and /web/settings (Settings tab
 * preselected) — so neither nav item dead-ends. This is the route scaffolding:
 * the live audit feed / persisted settings (real API) are a deliberate
 * follow-up, so until then the page renders the demo fixtures in demo builds
 * and its fail-safe empty states otherwise, inside a real, navigable frame.
 */
import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import WebShell from "../../components/web/WebShell";
import AuditSettingsPage from "./AuditSettingsPage";
import {
  DEMO_DATA_ENABLED,
  getDemoAuditTrail,
  getDemoAlertThresholds,
} from "./demoData";

export default function WebAuditSettingsRoute({ section = "audit" }) {
  const navigate = useNavigate();
  const isSettings = section === "settings";
  // Capture one timestamp so demo-trail generation and the `now` prop agree —
  // relative-time UI drifts if each call reads the clock separately.
  const now = Date.now();
  const entries = DEMO_DATA_ENABLED ? getDemoAuditTrail(now) : [];
  const thresholds = DEMO_DATA_ENABLED ? getDemoAlertThresholds() : [];
  return (
    <WebShell
      activeKey={isSettings ? "settings" : "audit"}
      onNavigate={(navSection) => {
        if (navSection && navSection.path) navigate(navSection.path);
      }}
      title={isSettings ? "Settings" : "Audit Log"}
    >
      <AuditSettingsPage
        entries={entries}
        thresholds={thresholds}
        now={now}
        initialTab={isSettings ? "settings" : "audit"}
      />
    </WebShell>
  );
}

WebAuditSettingsRoute.propTypes = {
  /** Which nav entry mounted this route — drives active nav, title, and tab. */
  section: PropTypes.oneOf(["audit", "settings"]),
};
